import React, {useEffect, useReducer, useState} from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {VideoPlayer} from 'app/components/tv/VideoPlayer';
import {Helmet} from 'react-helmet-async';
import {addPlaylistVideo, fetchTvPlaylist, updateCurrentVideo} from 'app/routes/tv/TvPlaylistPageActions';
import {TvPlaylistPageReducer} from 'app/routes/tv/TvPlaylistPageReducer';
import {useParams, useHistory} from 'react-router';
import {useFetchData, useRestoreData} from 'app/hooks/dataHooks';
import {getMetaData} from 'app/components/tv/helpers/getMetaData';
import {useInitImaScript} from 'app/components/tv/helpers/useInitImaScript';
import {useAdsConsent} from 'app/components/tv/helpers/useAdsConsent';
import {getRedirectVideo} from 'app/components/tv/helpers/lastViewedVideos';
import {useWebSockets} from 'app/hooks/useWebSockets';
import {selectWebsocketData} from 'app/hooks/reduxCreateSelectorHooks';
import {VideoBlocked} from 'app/components/tv/VideoBlocked';

const TvPlaylistPage = () => {
  const {videoSceneSlug, matchPlaylistSlug} = useParams();
  const {restoredData} = useRestoreData('TvPlaylistPage');
  const initialState = restoredData ?? {
    isFetching: false,
    items: [],
    currentVideo: {},
  };
  const [state, dispatch] = useReducer(TvPlaylistPageReducer, initialState);
  const dispatchRedux = useDispatch();
  const {getState} = useStore();

  const loadData = () => fetchTvPlaylist(matchPlaylistSlug, videoSceneSlug, dispatchRedux, getState)(dispatch, state);
  const {items: playlist, currentVideo, isFetching} = state;
  useFetchData(state, loadData, 'TvPlaylistPage');

  const history = useHistory();

  const {title, meta, schema} = getMetaData(playlist, videoSceneSlug, history.location.pathname);
  const imaScriptLoaded = useInitImaScript();
  const adsConsent = useAdsConsent();

  const websocketData = useSelector(selectWebsocketData);
  const [subscribed, setSubscribed] = useState(false);
  const conditionalTrigger =
    playlist?.[0]?.match?.section !== 'POST' && playlist?.[0]?.match?.flags?.includes('fupatv');
  const {websocketOpen, subscribeToChannel} = useWebSockets(dispatchRedux, conditionalTrigger);

  useEffect(() => {
    if (websocketOpen && playlist.length && !subscribed) {
      const playlistId = playlist[0].matchPlaylistId;
      subscribeToChannel(`playlist.${playlistId}`);
      setSubscribed(true);
    }
  }, [websocketOpen, playlist, subscribed]);

  useEffect(() => {
    if (websocketData?.event === 'video.created') {
      dispatch(addPlaylistVideo(websocketData.data));
    }
  }, [websocketData]);

  // Redirect Playlist-URL without scene to first scene
  useEffect(() => {
    if (!videoSceneSlug && playlist.length) {
      const newCurrentVideo = getRedirectVideo(playlist);
      if (newCurrentVideo.slug !== currentVideo.slug) {
        dispatch(updateCurrentVideo(newCurrentVideo));
      }
      history.replace(`/tv/match/${matchPlaylistSlug}/${newCurrentVideo.slug}`);
    }
  }, [playlist]);

  return (
    <>
      <Helmet title={title} meta={meta}>
        <script type='application/ld+json'>{schema}</script>
      </Helmet>
      {adsConsent === 'failure' ? (
        <VideoBlocked video={currentVideo} />
      ) : (
        <VideoPlayer
          video={currentVideo}
          playlist={playlist}
          isFetching={isFetching}
          imaScriptLoaded={imaScriptLoaded}
          dispatch={dispatch}
        />
      )}
    </>
  );
};

export default TvPlaylistPage;
